import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  EmojiEvents as TrophyIcon,
  CheckCircle as CheckCircleIcon,
  Category as CategoryIcon,
  Notes as NotesIcon,
  Inventory as InventoryIcon,
  Timeline as TimelineIcon,
  ContactPhone as ContactPhoneIcon,
  RateReview as RateReviewIcon,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { dataService } from '../services/dataService';

// ============================================================================
// CONSTANTES PARA localStorage (sincronizadas con otros módulos)
// ============================================================================
const STORAGE_KEYS = {
  ASSIGNED_CATEGORIES: 'interbarge_supplier_categories',
  SUPPLIER_NOTES: 'interbarge_supplier_notes',
  POTENTIAL_SUPPLIERS: 'interbarge_potential_suppliers',
  EVALUATIONS: 'interbarge_supplier_evaluations',
};

// ============================================================================
// HELPERS PARA LEER DATOS DE OTROS MÓDULOS
// ============================================================================

/**
 * Carga categorías asignadas desde ActiveSuppliers
 */
const loadAssignedCategories = (): Record<string, string[]> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.ASSIGNED_CATEGORIES);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[Dashboard] Error cargando categorías asignadas:', error);
    return {};
  }
};

/**
 * Carga notas desde ActiveSuppliers
 */
const loadNotes = (): Record<string, any[]> => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.SUPPLIER_NOTES);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('[Dashboard] Error cargando notas:', error);
    return {};
  }
};

/**
 * Carga proveedores potenciales desde PotentialSuppliers
 */
const loadPotentialSuppliers = (): any[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.POTENTIAL_SUPPLIERS);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    // Convertir fechas
    return parsed.map((s: any) => ({
      ...s,
      createdAt: new Date(s.createdAt),
      updatedAt: new Date(s.updatedAt),
      stageChangedAt: new Date(s.stageChangedAt),
      expectedCloseDate: s.expectedCloseDate ? new Date(s.expectedCloseDate) : undefined,
    }));
  } catch (error) {
    console.error('[Dashboard] Error cargando proveedores potenciales:', error);
    return [];
  }
};

/**
 * Carga evaluaciones desde Evaluation
 */
const loadEvaluations = (): any[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.EVALUATIONS);
    console.log(`[Dashboard] loadEvaluations: stored raw =`, stored);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    console.log(`[Dashboard] loadEvaluations: parsed =`, parsed);
    // Convertir fechas
    const result = parsed.map((e: any) => ({
      ...e,
      evaluatedAt: new Date(e.evaluatedAt),
    }));
    console.log(`[Dashboard] loadEvaluations: result (${result.length} items) =`, result);
    return result;
  } catch (error) {
    console.error('[Dashboard] Error cargando evaluaciones:', error);
    return [];
  }
};


interface DashboardStats {
  totalSuppliers: number;
  totalOrders: number;
  totalSpendUSD: number;
  averageOrderValue: number;
  totalCategories: number;
  totalLocations: number;
  // Nuevos KPIs para gestión de proveedores
  suppliersWithCategories: number;
  criticalCategoriesLowSuppliers: number;
  percentageCategorizied: number;
  avgCategoriesPerSupplier: number;
  totalNotes: number;
  notesThisMonth: number;
  suppliersWithNotes: number;
  totalCommercialContacts: number; // NUEVO
  // KPIs de pipeline
  totalPotentialSuppliers: number;
  // KPIs de evaluación
  totalEvaluatedSuppliers: number;
  avgEvaluationScore: number;
  strategicCount: number;
  preferredCount: number;
  developingCount: number;
  conditionalCount: number;
  disabledCount: number;
}

interface SingleSupplierCategory {
  category: string;
  supplier: string;
  orderCount: number;
  totalSpend: number;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalSuppliers: 0,
    totalOrders: 0,
    totalSpendUSD: 0,
    averageOrderValue: 0,
    totalCategories: 0,
    totalLocations: 0,
    suppliersWithCategories: 0,
    criticalCategoriesLowSuppliers: 0,
    percentageCategorizied: 0,
    avgCategoriesPerSupplier: 0,
    totalNotes: 0,
    notesThisMonth: 0,
    suppliersWithNotes: 0,
    totalCommercialContacts: 0, // NUEVO
    totalPotentialSuppliers: 0,
    totalEvaluatedSuppliers: 0,
    avgEvaluationScore: 0,
    strategicCount: 0,
    preferredCount: 0,
    developingCount: 0,
    conditionalCount: 0,
    disabledCount: 0,
  });

  const [singleSupplierCategories, setSingleSupplierCategories] = useState<SingleSupplierCategory[]>([]);
  const [totalSingleSupplierCategories, setTotalSingleSupplierCategories] = useState<number>(0);
  const [topCategoriesBySupplierCount, setTopCategoriesBySupplierCount] = useState<any[]>([]);
  const [potentialSuppliersByStage, setPotentialSuppliersByStage] = useState<any[]>([]);
  
  const location = useLocation();

  // Efecto para recargar datos cuando volvemos a la ruta del Dashboard
  useEffect(() => {
    console.log('🔄 Dashboard: Recargando datos (cambio de ruta o mount)');
    loadDashboardData();
  }, [location.pathname]); // Se ejecuta cada vez que cambia la ruta

  useEffect(() => {
    // Listener para recargar cuando vuelves a la página (cambio de tab o focus)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        console.log('🔄 Dashboard: Recargando datos (página visible)');
        loadDashboardData();
      }
    };
    
    // Listener para storage events (cuando otros tabs modifican localStorage)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key && e.key.startsWith('interbarge_')) {
        console.log(`🔄 Dashboard: Recargando datos (cambio en ${e.key})`);
        loadDashboardData();
      }
    };
    
    // Listener para evento custom de evaluaciones actualizadas
    const handleEvaluationsUpdated = () => {
      console.log('🔄 Dashboard: Recargando datos (evaluaciones actualizadas)');
      loadDashboardData();
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('interbarge-evaluations-updated', handleEvaluationsUpdated);
    
    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('interbarge-evaluations-updated', handleEvaluationsUpdated);
    };
  }, []);

  const loadDashboardData = () => {
    const orders = dataService.getPurchaseOrders();
    const suppliers = dataService.getSuppliers();
    const categories = dataService.getCategories();
    const locations = dataService.getLocations();

    // Calcular estadísticas generales
    const totalSpend = orders.reduce((sum, order) => sum + order.amountUSD, 0);
    const avgOrder = orders.length > 0 ? totalSpend / orders.length : 0;

    // === NUEVOS KPIs: Categorías Asignadas ===
    // Primero intentar desde localStorage (categorías asignadas manualmente)
    const assignedCategories = loadAssignedCategories();
    const suppliersWithManualCats = Object.keys(assignedCategories).filter(
      (supplier) => assignedCategories[supplier] && assignedCategories[supplier].length > 0
    );
    
    // Si no hay categorías asignadas manualmente, calcular desde el CSV
    // (proveedores que compraron en al menos 1 categoría)
    let totalSuppliersWithCategories = suppliersWithManualCats.length;
    let totalCategoriesAssigned = suppliersWithManualCats.reduce(
      (sum, supplier) => sum + assignedCategories[supplier].length, 
      0
    );
    
    // Si localStorage está vacío, usar datos del CSV
    if (totalSuppliersWithCategories === 0) {
      // Calcular cuántas categorías únicas compró cada proveedor
      const supplierCategories = new Map<string, Set<string>>();
      orders.forEach(order => {
        if (!supplierCategories.has(order.supplier)) {
          supplierCategories.set(order.supplier, new Set());
        }
        supplierCategories.get(order.supplier)!.add(order.item);
      });
      
      // Contar proveedores con al menos 1 categoría (todos los del CSV)
      totalSuppliersWithCategories = supplierCategories.size;
      totalCategoriesAssigned = Array.from(supplierCategories.values())
        .reduce((sum, catSet) => sum + catSet.size, 0);
    }
    
    const percentageCategorized = suppliers.length > 0 
      ? (totalSuppliersWithCategories / suppliers.length) * 100 
      : 0;
    
    // Calcular promedio de categorías por proveedor
    const avgCatsPerSupplier = totalSuppliersWithCategories > 0 
      ? totalCategoriesAssigned / totalSuppliersWithCategories 
      : 0;

    // === NUEVO KPI: Categorías Contables Críticas con Pocos Proveedores (<5) ===
    // Agrupar por cuenta contable y contar proveedores únicos
    const accountingAccountStats = new Map<string, Set<string>>();
    orders.forEach(order => {
      if (!order.accountingAccount) return;
      if (!accountingAccountStats.has(order.accountingAccount)) {
        accountingAccountStats.set(order.accountingAccount, new Set());
      }
      accountingAccountStats.get(order.accountingAccount)!.add(order.supplier);
    });
    
    // Contar cuentas contables con menos de 5 proveedores
    const criticalAccounts = Array.from(accountingAccountStats.values())
      .filter(suppliers => suppliers.size < 5).length;

    // === NUEVO GRÁFICO: Categorías (Items) con 1 solo proveedor ===
    const categoryStats = dataService.getCategoryStats();
    const singleSupplierCats = categoryStats
      .filter(cat => cat.supplierCount === 1)
      .sort((a, b) => b.totalSpend - a.totalSpend)
      .slice(0, 15) // Top 15 categorías con 1 proveedor
      .map(cat => {
        // Encontrar el nombre del proveedor único
        const orders = dataService.getPurchaseOrders().filter(o => o.item === cat.category);
        const uniqueSupplier = orders[0]?.supplier || 'Desconocido';
        return {
          category: cat.category.length > 30 ? cat.category.substring(0, 30) + '...' : cat.category,
          supplier: uniqueSupplier.length > 25 ? uniqueSupplier.substring(0, 25) + '...' : uniqueSupplier,
          orderCount: cat.orderCount,
          totalSpend: cat.totalSpend,
        };
      });
    setSingleSupplierCategories(singleSupplierCats);
    setTotalSingleSupplierCategories(categoryStats.filter(cat => cat.supplierCount === 1).length);

    // === NUEVOS KPIs: Notas ===
    const notesData = loadNotes();
    const allNotes = Object.values(notesData).flat();
    const totalNotesCount = allNotes.length;
    const suppliersWithNotesCount = Object.keys(notesData).filter(
      (supplier) => notesData[supplier] && notesData[supplier].length > 0
    ).length;

    // Notas de este mes
    const now = new Date();
    const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const notesThisMonthCount = allNotes.filter((note: any) => {
      const noteDate = new Date(note.timestamp);
      return noteDate >= firstDayOfMonth;
    }).length;

    // === NUEVOS KPIs: Proveedores Potenciales (Pipeline) ===
    const potentialSuppliers = loadPotentialSuppliers();
    const totalPotential = potentialSuppliers.length;

    // === NUEVOS KPIs: Evaluaciones ===
    const evaluations = loadEvaluations();
    console.log(`[Dashboard] Total evaluations loaded: ${evaluations.length}`);
    
    // Contar proveedores únicos evaluados (un proveedor puede tener múltiples evaluaciones)
    const uniqueEvaluatedSuppliers = new Set(evaluations.map((e: any) => e.supplierId));
    const totalEvaluated = uniqueEvaluatedSuppliers.size;
    console.log(`[Dashboard] Unique evaluated suppliers: ${totalEvaluated}`, Array.from(uniqueEvaluatedSuppliers));
    
    // Score promedio de todas las evaluaciones
    const avgScore = evaluations.length > 0 
      ? evaluations.reduce((sum: number, e: any) => sum + e.totalScore, 0) / evaluations.length 
      : 0;
    console.log(`[Dashboard] Average score: ${avgScore.toFixed(2)}`);
    
    // Contar por rating (última evaluación por proveedor)
    const latestEvaluationsBySupplier = new Map();
    evaluations.forEach((e: any) => {
      const existing = latestEvaluationsBySupplier.get(e.supplierId);
      if (!existing || new Date(e.evaluatedAt) > new Date(existing.evaluatedAt)) {
        latestEvaluationsBySupplier.set(e.supplierId, e);
      }
    });
    
    const latestEvaluations = Array.from(latestEvaluationsBySupplier.values());
    console.log(`[Dashboard] Latest evaluations:`, latestEvaluations);
    
    const strategic = latestEvaluations.filter((e: any) => e.rating === 'strategic').length;
    const preferred = latestEvaluations.filter((e: any) => e.rating === 'preferred').length;
    const developing = latestEvaluations.filter((e: any) => e.rating === 'developing').length;
    const conditional = latestEvaluations.filter((e: any) => e.rating === 'conditional').length;
    const disabled = latestEvaluations.filter((e: any) => e.rating === 'disabled').length;
    
    console.log(`[Dashboard] Ratings breakdown: Strategic=${strategic}, Preferred=${preferred}, Developing=${developing}, Conditional=${conditional}, Disabled=${disabled}`);

    // === NUEVO GRÁFICO 2: Top Categorías por Cantidad de Proveedores ===
    const topCategoriesBySuppCount = categoryStats
      .sort((a, b) => b.supplierCount - a.supplierCount)
      .slice(0, 10)
      .map(cat => ({
        category: cat.category.length > 30 ? cat.category.substring(0, 30) + '...' : cat.category,
        supplierCount: cat.supplierCount,
        orderCount: cat.orderCount,
      }));
    setTopCategoriesBySupplierCount(topCategoriesBySuppCount);

    // === NUEVO GRÁFICO 3: % Proveedores Potenciales por Estado ===
    const stageLabels: Record<string, string> = {
      lead: 'Lead',
      qualified: 'Calificado',
      proposal: 'Propuesta',
      negotiation: 'Negociación',
      approved: 'Aprobado',
      rejected: 'Rechazado',
    };
    const stageColors: Record<string, string> = {
      lead: '#9e9e9e',
      qualified: '#2196f3',
      proposal: '#ff9800',
      negotiation: '#ffc107',
      approved: '#4caf50',
      rejected: '#f44336',
    };
    const stageGroups = potentialSuppliers.reduce((acc: any, supplier: any) => {
      acc[supplier.stage] = (acc[supplier.stage] || 0) + 1;
      return acc;
    }, {});
    const potentialByStage = Object.entries(stageGroups).map(([stage, count]) => ({
      name: stageLabels[stage] || stage,
      value: count,
      percentage: totalPotential > 0 ? ((count as number) / totalPotential * 100).toFixed(1) : '0',
      color: stageColors[stage] || '#9e9e9e',
    }));
    setPotentialSuppliersByStage(potentialByStage);

    // === NUEVO KPI: Contactos Comerciales (solo Proveedores Potenciales) ===
    const totalCommercialContacts = totalPotential;

    setStats({
      totalSuppliers: suppliers.length,
      totalOrders: orders.length,
      totalSpendUSD: totalSpend,
      averageOrderValue: avgOrder,
      totalCategories: categories.length,
      totalLocations: locations.length,
      suppliersWithCategories: totalSuppliersWithCategories,
      criticalCategoriesLowSuppliers: criticalAccounts,
      percentageCategorizied: percentageCategorized,
      avgCategoriesPerSupplier: avgCatsPerSupplier,
      totalNotes: totalNotesCount,
      notesThisMonth: notesThisMonthCount,
      suppliersWithNotes: suppliersWithNotesCount,
      totalCommercialContacts: totalCommercialContacts, // NUEVO
      totalPotentialSuppliers: totalPotential,
      totalEvaluatedSuppliers: totalEvaluated,
      avgEvaluationScore: avgScore,
      strategicCount: strategic,
      preferredCount: preferred,
      developingCount: developing,
      conditionalCount: conditional,
      disabledCount: disabled,
    });
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatNumber = (value: number): string => {
    return new Intl.NumberFormat('es-AR').format(value);
  };

  // KPI Card Component
  const KPICard = ({ title, value, icon, color }: { title: string; value: string; icon: React.ReactNode; color: string }) => (
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', color }}>
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              bgcolor: `${color}15`,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Panel de control con métricas principales del sistema de gestión de proveedores
        </Typography>
      </Box>

      {/* KPIs Principales */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Total Proveedores"
            value={formatNumber(stats.totalSuppliers)}
            icon={<BusinessIcon sx={{ fontSize: 40, color: '#b32317' }} />}
            color="#b32317"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Contactos Comerciales"
            value={formatNumber(stats.totalCommercialContacts)}
            icon={<ContactPhoneIcon sx={{ fontSize: 40, color: '#00bcd4' }} />}
            color="#00bcd4"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Proveedores Evaluados"
            value={formatNumber(stats.totalEvaluatedSuppliers)}
            icon={<RateReviewIcon sx={{ fontSize: 40, color: '#002d62' }} />}
            color="#002d62"
          />
        </Grid>
      </Grid>

      {/* Título sección de gestión */}
      <Box sx={{ mb: 2, mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Gestión de Proveedores
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Métricas de categorización, notas y proveedores en pipeline
        </Typography>
      </Box>

      {/* KPIs de Gestión de Proveedores */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Proveedores con Categorías"
            value={`${formatNumber(stats.suppliersWithCategories)} (${stats.percentageCategorizied.toFixed(1)}%)`}
            icon={<CategoryIcon sx={{ fontSize: 40, color: '#4caf50' }} />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Categorías Críticas (<5 Proveedores)"
            value={formatNumber(stats.criticalCategoriesLowSuppliers)}
            icon={<InventoryIcon sx={{ fontSize: 40, color: '#ff5722' }} />}
            color="#ff5722"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Promedio Categorías/Proveedor"
            value={stats.avgCategoriesPerSupplier.toFixed(1)}
            icon={<TimelineIcon sx={{ fontSize: 40, color: '#002d62' }} />}
            color="#002d62"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Total Notas"
            value={formatNumber(stats.totalNotes)}
            icon={<NotesIcon sx={{ fontSize: 40, color: '#9c27b0' }} />}
            color="#9c27b0"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Notas Este Mes"
            value={formatNumber(stats.notesThisMonth)}
            icon={<NotesIcon sx={{ fontSize: 40, color: '#ff9800' }} />}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Proveedores Potenciales (Pipeline)"
            value={formatNumber(stats.totalPotentialSuppliers)}
            icon={<TrendingUpIcon sx={{ fontSize: 40, color: '#00bcd4' }} />}
            color="#00bcd4"
          />
        </Grid>
      </Grid>

      {/* Título sección de análisis de riesgo */}
      <Box sx={{ mb: 2, mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Análisis de Riesgo de Concentración
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Categorías con dependencia de un único proveedor (riesgo crítico)
        </Typography>
      </Box>

      {/* Gráficos de Análisis */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Gráfico 1: Categorías con 1 solo proveedor */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: 16 }}>
              Top 15 Categorías con 1 Solo Proveedor
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Total categorías riesgo crítico: <strong>{totalSingleSupplierCategories}</strong>
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={singleSupplierCategories} layout="vertical" margin={{ left: 120, right: 10, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" tickFormatter={(value) => `$${(value / 1000).toFixed(0)}K`} />
                <YAxis type="category" dataKey="category" width={110} style={{ fontSize: 9 }} />
                <Tooltip 
                  formatter={(value: number, name: string) => {
                    if (name === 'totalSpend') return formatCurrency(value);
                    return value;
                  }}
                  labelFormatter={(label) => `Categoría: ${label}`}
                />
                <Bar dataKey="totalSpend" fill="#ff5722" name="Gasto Total USD" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Gráfico 2: Top Categorías por cantidad de proveedores */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: 16 }}>
              Top 10 Categorías por # Proveedores
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Categorías con mayor diversificación de proveedores
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={topCategoriesBySupplierCount} layout="vertical" margin={{ left: 120, right: 10, top: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis type="category" dataKey="category" width={110} style={{ fontSize: 9 }} />
                <Tooltip 
                  formatter={(value: number) => value}
                  labelFormatter={(label) => `Categoría: ${label}`}
                />
                <Bar dataKey="supplierCount" fill="#4caf50" name="Cantidad de Proveedores" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Gráfico 3: Proveedores potenciales por estado */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontSize: 16 }}>
              Proveedores Potenciales por Estado
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Distribución del pipeline de proveedores
            </Typography>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                <Pie
                  data={potentialSuppliersByStage}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {potentialSuppliersByStage.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(value: number) => value} />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
      </Grid>

      {/* Título sección de evaluación */}
      <Box sx={{ mb: 2, mt: 4 }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600 }}>
          Evaluación de Proveedores
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Métricas de performance y calidad de proveedores evaluados
        </Typography>
      </Box>

      {/* KPIs de Evaluación */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Proveedores Evaluados"
            value={formatNumber(stats.totalEvaluatedSuppliers)}
            icon={<AssessmentIcon sx={{ fontSize: 40, color: '#002d62' }} />}
            color="#002d62"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Score Promedio General"
            value={stats.avgEvaluationScore.toFixed(2) + '/5.00'}
            icon={<TrophyIcon sx={{ fontSize: 40, color: '#ff9800' }} />}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Estratégicos"
            value={formatNumber(stats.strategicCount)}
            icon={<CheckCircleIcon sx={{ fontSize: 40, color: '#2e7d32' }} />}
            color="#2e7d32"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Preferidos"
            value={formatNumber(stats.preferredCount)}
            icon={<CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50' }} />}
            color="#4caf50"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="En Desarrollo"
            value={formatNumber(stats.developingCount)}
            icon={<TrendingUpIcon sx={{ fontSize: 40, color: '#ff9800' }} />}
            color="#ff9800"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <KPICard
            title="Condicional + Inhabilitado"
            value={formatNumber(stats.conditionalCount + stats.disabledCount)}
            icon={<BusinessIcon sx={{ fontSize: 40, color: stats.conditionalCount + stats.disabledCount > 0 ? '#d32f2f' : '#9e9e9e' }} />}
            color={stats.conditionalCount + stats.disabledCount > 0 ? '#d32f2f' : '#9e9e9e'}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
